/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useSelector } from 'react-redux';
import { LoadingIndicatorProgressBar } from '@opengeoweb/core';
import { CustomTooltip } from '@opengeoweb/shared';
import { loadingIndicatorSelectors } from '@opengeoweb/store';
import { CloudIdle, CloudLoading } from '@opengeoweb/theme';
import type { AppStore } from '../../../store/types';
import { useWorkspaceTranslation } from '../../../utils/i18n';

interface LoadingIndicatorConnectProps {
  panelId: string;
}

export const LoadingIndicatorConnect: React.FC<
  LoadingIndicatorConnectProps
> = ({ panelId }: LoadingIndicatorConnectProps) => {
  const isLoading = useSelector((store: AppStore) =>
    loadingIndicatorSelectors.isLoading(store, panelId),
  );

  const getMapLoadingPercentage = useSelector((store: AppStore) =>
    loadingIndicatorSelectors.getMapLoadingPercentage(store, panelId),
  );
  const { t } = useWorkspaceTranslation();

  return (
    <CustomTooltip
      title={
        isLoading
          ? t('workspace-loading-indicator-loading')
          : t('workspace-loading-indicator-idle')
      }
    >
      <span style={{ height: '100%' }}>
        <span style={{ position: 'relative', zIndex: 2 }}>
          {isLoading ? (
            <CloudLoading
              data-testid="cloudicon-loading"
              style={{ fill: '#186DFF' }}
            />
          ) : (
            <CloudIdle data-testid="cloudicon-idle" />
          )}
        </span>
        {getMapLoadingPercentage !== 100 && (
          <LoadingIndicatorProgressBar
            progressPercentage={getMapLoadingPercentage}
          />
        )}
      </span>
    </CustomTooltip>
  );
};
